import { useState, type MouseEventHandler } from 'react'
import Icon from './Icon'
import { navListRootIndices } from '../../../../platform/client/navigation'
import Purchase from './Purchase'
import { t } from '../../../../platform/i18n'
import { push } from '../../../../platform/client/history'

import './MenuBar.css'

type MenuSubItem = {
  name: string
  propId: string
}

type MenuItem = {
  name: string
  propId: string
  children?: MenuSubItem[]
}

type NavListItem = {
  name: string
  propId: string
  childIndices: number[]
}

function getMenuItems(navList: Record<number, NavListItem>): MenuItem[] {
  return (
    navListRootIndices
      // first category is the "product finder" which needs to be treated differently
      .slice(1)
      .map((i) => navList[i])
      .flatMap((category) =>
        category.childIndices
          .map((j) => navList[j])
          .filter((option) => option.isAvailable)
          .map((option) => {
            if (option.childIndices.length === 0) {
              return option
            }
            return {
              ...option,
              children: option.childIndices
                .map((k) => navList[k])
                .filter((subOption) => subOption.isAvailable),
            }
          }),
      )
  )
}

function getProductFinder(navList) {
  const titleNode = navList[navList[navListRootIndices[0]].childIndices[0]]

  const subItems = titleNode.childIndices.map((i) => {
    const child = navList[i]
    return { name: child.name, id: child.id }
  })
  return [titleNode.name, subItems]
}

const ProductFinder = ({ navList, navigateToWizardStep, resetValues }) => {
  const [title, subItems] = getProductFinder(navList)
  return (
    <div className="section">
      <div className="title">{title}</div>
      <div className="sub-items">
        <div style={{ cursor: 'pointer' }} onClick={resetValues}>
          {t('_rawlings:startOver')}
          <Icon name="start-over" />
        </div>
        {subItems.map((subItem) => (
          <div
            style={{ cursor: 'pointer' }}
            onClick={() => navigateToWizardStep(subItem.id)}
          >
            {subItem.name}
          </div>
        ))}
      </div>
    </div>
  )
}

const Menu = ({
  navList,
  setMenuOpen,
  onCart,
  onSummary,
  product,
  navigateToWizardStep,
  resetValues,
}) => {
  const menuItems = getMenuItems(navList)
  return (
    <div className="menu">
      <div className="overlay" onClick={() => setMenuOpen(false)} />
      <div className="menu-container">
        <div className="menu-items">
          <ProductFinder
            resetValues={resetValues}
            navList={navList}
            navigateToWizardStep={navigateToWizardStep}
          />
          {menuItems.map((item) => {
            const hasChildren = !!item.children && item.children.length > 0
            const titleHref =
              hasChildren ? `#${item.children![0].propId}` : `#${item.propId}`
            return (
              <div className="section">
                <div onClick={() => setMenuOpen(false)} className="title">
                  <a href={titleHref}>{item.name}</a>
                </div>
                {hasChildren && (
                  <div className="sub-items">
                    {item.children!.map((subItem) => (
                      <div onClick={() => setMenuOpen(false)}>
                        <a key={subItem.propId} href={`#${subItem.propId}`}>
                          {subItem.name}
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
        <div className="purchase-container">
          <Purchase
            isActive
            product={product}
            onCart={onCart}
            onSummary={onSummary}
          />
        </div>
      </div>
    </div>
  )
}

type Props = {
  navList: Record<number, NavListItem>
  navigateToWizardStep: (id: string) => void
  onCart: MouseEventHandler<HTMLButtonElement>
  onSummary: MouseEventHandler<HTMLButtonElement>
  product: unknown
  resetValues: () => void
  returnToSiteName?: string
  returnToSiteUrl?: string
  showMenuButtons?: boolean
}

const MenuBar = ({
  navList,
  navigateToWizardStep,
  onCart,
  onSummary,
  product,
  resetValues,
  returnToSiteName,
  returnToSiteUrl,
  showMenuButtons,
}: Props) => {
  const [isMenuOpen, setMenuOpen] = useState(false)
  return (
    <>
      <div className="menu-bar">
        <div>
          {returnToSiteUrl && (
            <a href={returnToSiteUrl} className="return-to-site-url">
              <span className="return-icon">
                <Icon name="arrow-back" inverted />
              </span>
              <span className="return-url">Back to {returnToSiteName}</span>
            </a>
          )}
        </div>
        {showMenuButtons && (
          <div className="button-container">
            <button
              className="menu-button"
              onClick={() => setMenuOpen((isOpen) => !isOpen)}
            >
              <div>Menu</div>
              <div className="icon-container">
                <Icon name="bars" inverted />
              </div>
            </button>
            <button className="add-to-cart-button" onClick={onCart}>
              Add to Cart
              <Icon name="cart-filled" />
            </button>
          </div>
        )}
      </div>
      {isMenuOpen && (
        <Menu
          navList={navList}
          setMenuOpen={setMenuOpen}
          navigateToWizardStep={(id) => {
            setMenuOpen(false)
            navigateToWizardStep(id)
          }}
          onCart={onCart}
          onSummary={(e) => {
            setMenuOpen(false)
            onSummary(e)
          }}
          product={product}
          resetValues={() => {
            resetValues()
            push({ path: '/', query: null })
            setMenuOpen(false)
          }}
        />
      )}
    </>
  )
}

export default MenuBar
